// src/pages/MentorDashboardPage.js
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert, Badge, Button, Card, Col, Row, Spinner, Table,
} from "react-bootstrap";
import api from "../api";

const NAV_STATUS_VARIANT = {
  active:      "success",
  approved:    "primary",
  recommended: "warning",
  denied:      "danger",
};

function NavStatusBadge({ status }) {
  const bg = NAV_STATUS_VARIANT[status] || "secondary";
  return (
    <Badge bg={bg} text={bg === "warning" ? "dark" : undefined}>
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : "—"}
    </Badge>
  );
}

const SERVICE_STATUS_VARIANT = {
  Open: "secondary",
  "Pending Approval": "warning",
  "Pending Verification": "warning",
  Approved: "success",
  Denied: "danger",
};

function StatusBadge({ status }) {
  if (status === "Complete") {
    return <span className="badge" style={{ backgroundColor: "#6f42c1", color: "#fff" }}>{status}</span>;
  }
  const bg = SERVICE_STATUS_VARIANT[status] || "secondary";
  return (
    <Badge bg={bg} text={bg === "warning" ? "dark" : undefined}>
      {status}
    </Badge>
  );
}

export default function MentorDashboardPage() {
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api.get("/dashboard/mentor");
      setData(res.data);
    } catch {
      setError("Failed to load mentor dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center py-5"><Spinner animation="border" /></div>;
  if (error)   return <Alert variant="danger" className="m-3">{error}</Alert>;

  const { clients, open_services, totals } = data;

  return (
    <div className="p-3">
      <h4 className="mb-4">
        <i className="bi bi-person-heart me-2" />
        Mentor Dashboard
      </h4>

      {/* ── Summary Cards ── */}
      <Row className="g-3 mb-4">
        <Col xs={6} sm={3}>
          <Card className="text-center shadow-sm h-100">
            <Card.Body>
              <div style={{ fontSize: "2rem", fontWeight: 700 }}>{totals.clients}</div>
              <div className="text-muted small">My Clients</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={3}>
          <Card className="text-center shadow-sm h-100">
            <Card.Body>
              <div style={{ fontSize: "2rem", fontWeight: 700, color: "var(--bs-success)" }}>
                {totals.active}
              </div>
              <div className="text-muted small">Active</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={3}>
          <Card className="text-center shadow-sm h-100">
            <Card.Body>
              <div style={{ fontSize: "2rem", fontWeight: 700, color: "var(--bs-primary)" }}>
                {totals.approved}
              </div>
              <div className="text-muted small">Approved</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={3}>
          <Card className="text-center shadow-sm h-100">
            <Card.Body>
              <div style={{ fontSize: "2rem", fontWeight: 700, color: "var(--bs-warning)" }}>
                {totals.open_tasks}
              </div>
              <div className="text-muted small">Open Tasks</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        {/* ── My Clients ── */}
        <Col xs={12} lg={6}>
          <Card className="shadow-sm h-100">
            <Card.Header className="fw-semibold d-flex align-items-center gap-2">
              <i className="bi bi-people-fill me-1" />
              My Clients
              <Badge bg="secondary" className="ms-auto">{clients.length}</Badge>
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: "420px", overflowY: "auto" }}>
              {clients.length === 0 ? (
                <p className="text-muted p-3 mb-0">No clients assigned to you yet.</p>
              ) : (
                <Table hover responsive className="mb-0" size="sm">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th>Client</th>
                      <th>Status</th>
                      <th className="text-center">Open Tasks</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((c) => (
                      <tr key={c.id}>
                        <td className="fw-semibold">{c.name}</td>
                        <td><NavStatusBadge status={c.nav_status} /></td>
                        <td className="text-center">
                          {c.open_tasks > 0
                            ? <Badge bg="warning" text="dark">{c.open_tasks}</Badge>
                            : <span className="text-muted">—</span>}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => navigate(`/clients/${c.id}/view`)}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* ── Upcoming Meetings ── */}
        <Col xs={12} lg={6}>
          <Card className="shadow-sm h-100">
            <Card.Header className="fw-semibold">
              <i className="bi bi-calendar-event me-2" />
              Upcoming Meetings
            </Card.Header>
            <Card.Body className="d-flex flex-column align-items-center justify-content-center text-center text-muted py-5">
              <i className="bi bi-calendar2-plus" style={{ fontSize: "2rem", opacity: 0.4 }} />
              <div className="mt-2">Meeting tracking coming soon.</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Open Tasks ── */}
      <Card className="shadow-sm">
        <Card.Header className="fw-semibold d-flex align-items-center gap-2">
          <i className="bi bi-list-task me-1" />
          Open Tasks — My Clients
          {open_services.length > 0 && (
            <Badge bg="warning" text="dark" className="ms-auto">{open_services.length}</Badge>
          )}
        </Card.Header>
        <Card.Body className="p-0">
          {open_services.length === 0 ? (
            <p className="text-muted p-3 mb-0">No open tasks for your clients.</p>
          ) : (
            <Table hover responsive className="mb-0" size="sm">
              <thead className="table-light">
                <tr>
                  <th>Client</th>
                  <th>Service</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Assigned To</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {open_services.map((s) => (
                  <tr key={s.service_id}>
                    <td className="fw-semibold">
                      <Link to={`/clients/${s.client_id}/view`}>{s.client_name}</Link>
                    </td>
                    <td>{s.service_type}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{s.service_date || "—"}</td>
                    <td><StatusBadge status={s.status} /></td>
                    <td>{s.assigned_to}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => navigate(`/clients/${s.client_id}/view`, {
                          state: { openServiceId: s.service_id },
                        })}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
